import { fg } from '@atlassian/jira-feature-gating';
import {
	useIsStickyColumnsVisible,
	useIsAutoUnstickyOnSmallScreenEnabled,
} from '../features/selectors.tsx';
import { useScrollStateSelector } from '../scroll-state/index.tsx';

/**
 * Custom hook to determine whether the table should apply frozen columns.
 *
 * The purpose of this hook is to handle situations where the table size is small (less than 960px).
 * Even if the user has frozen columns enabled in their settings (`useIsStickyColumnsVisible` returns true),
 * we may want to auto-unfreeze columns for better UX on smaller screens.
 *
 * Horizontal scroll state is also considered to resolve SSR (Server-Side Render) and
 * client initial render UI inconsistencies:
 * - On SSR, we don't know the actual table width, so this hook (`useShouldApplyFrozenColumns`)
 *   will default to true only when the user has sticky columns in table.
 * - Once the user interacts (scrolls) on the client, we can then accurately determine screen size
 *   and adjust whether columns should actually be frozen or unfrozen.
 *
 * Logic:
 * - If sticky columns are visible:
 *   - If table is horizontally scrolled: unfreeze columns if it's a small screen
 *   - Otherwise (not scrolled): keep columns frozen
 */

const SMALL_TABLE_WIDTH = 960;

export const useShouldApplyFrozenColumns = () => {
	if (!fg('issue_table_frozen_columns')) {
		return {
			isStickyColumnsVisible: false,
			isSmallScreen: false,
		};
	}
	// eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
	const isAutoUnstickyOnSmallScreenEnabled = useIsAutoUnstickyOnSmallScreenEnabled();
	// eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
	const { hasScrolled, tableWidth } = useScrollStateSelector((state) => ({
		hasScrolled: state.hasScrolled,
		tableWidth: state.width,
	}));

	const isSmallScreen =
		isAutoUnstickyOnSmallScreenEnabled && tableWidth < SMALL_TABLE_WIDTH && tableWidth !== 0;
	// eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
	let isStickyColumnsVisible = useIsStickyColumnsVisible();

	if (isStickyColumnsVisible) {
		isStickyColumnsVisible = hasScrolled ? !isSmallScreen : true;
	}

	return {
		isStickyColumnsVisible,
		isSmallScreen,
	};
};
